'use client';

/**
 * Entities Page - Full UI with filtering, entity detail modal, bulk operations, and edit support
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { WorkspaceProvider, useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { CommandCenterProvider, useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import CreateEntityModal from '@/components/command-center/CreateEntityModal';
import EntityDetailModal from '@/components/command-center/EntityDetailModal';
import EntityFilters, { type EntityFilterValues } from '@/components/command-center/EntityFilters';
import BulkActionsToolbar from '@/components/command-center/BulkActionsToolbar';
import CommandCenterHeader from '@/components/command-center/CommandCenterHeader';
import { ENTITY_TYPE_LABELS } from '@/lib/command-center/types';
import type { Entity, EntityStatus } from '@/lib/command-center/types';

function EntitiesContent() {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const { entities, loading: ccLoading, workspaceId, setWorkspaceId, loadEntities, deleteEntity, updateEntity } = useCommandCenter();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter state
  const [filters, setFilters] = useState<EntityFilterValues>({
    search: '',
    types: [],
    status: 'all',
    tags: [],
  });

  // Set workspace ID when available
  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  // Extract all unique tags from entities
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    entities.forEach(entity => {
      entity.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [entities]);

  // Filter entities based on current filters
  const filteredEntities = useMemo(() => {
    return entities.filter(entity => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName = entity.name.toLowerCase().includes(searchLower);
        const matchesDescription = entity.description?.toLowerCase().includes(searchLower);
        const matchesTags = entity.tags?.some(tag => tag.toLowerCase().includes(searchLower));
        if (!matchesName && !matchesDescription && !matchesTags) {
          return false;
        }
      }

      // Type filter
      if (filters.types.length > 0 && !filters.types.includes(entity.entity_type)) {
        return false;
      }

      // Status filter
      if (filters.status !== 'all' && entity.status !== filters.status) {
        return false;
      }

      // Tags filter
      if (filters.tags.length > 0) {
        const entityTags = entity.tags || [];
        if (!filters.tags.some(tag => entityTags.includes(tag))) {
          return false;
        }
      }

      return true;
    });
  }, [entities, filters]);

  // Clear selections when entities change
   
  useEffect(() => {
    setSelectedIds(new Set());
  }, [entities]);
   

  // Selection handlers
  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredEntities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEntities.map(e => e.id)));
    }
  }, [filteredEntities, selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Get selected entities
  const selectedEntities = useMemo(() => {
    return entities.filter(e => selectedIds.has(e.id));
  }, [entities, selectedIds]);

  // Bulk action handlers
  const handleBulkDelete = useCallback(async () => {
    const promises = Array.from(selectedIds).map(id => deleteEntity(id));
    await Promise.all(promises);
    setSelectedIds(new Set());
    loadEntities();
  }, [selectedIds, deleteEntity, loadEntities]);

  const handleBulkStatusChange = useCallback(async (status: EntityStatus) => {
    const promises = selectedEntities.map(entity =>
      updateEntity(entity.id, { status })
    );
    await Promise.all(promises);
    setSelectedIds(new Set());
    loadEntities();
  }, [selectedEntities, updateEntity, loadEntities]);

  const handleBulkAddTags = useCallback(async (tagsToAdd: string[]) => {
    const promises = selectedEntities.map(entity => {
      const currentTags = entity.tags || [];
      const newTags = [...new Set([...currentTags, ...tagsToAdd])];
      return updateEntity(entity.id, { tags: newTags });
    });
    await Promise.all(promises);
    loadEntities();
  }, [selectedEntities, updateEntity, loadEntities]);

  const handleBulkRemoveTags = useCallback(async (tagsToRemove: string[]) => {
    const promises = selectedEntities.map(entity => {
      const currentTags = entity.tags || [];
      const newTags = currentTags.filter(t => !tagsToRemove.includes(t));
      return updateEntity(entity.id, { tags: newTags });
    });
    await Promise.all(promises);
    loadEntities();
  }, [selectedEntities, updateEntity, loadEntities]);

  const loading = workspaceLoading || ccLoading;
  const total = entities.length;
  const filteredCount = filteredEntities.length;
  const isFiltered = filters.search || filters.types.length > 0 || filters.status !== 'all' || filters.tags.length > 0;
  const allSelected = filteredEntities.length > 0 && selectedIds.size === filteredEntities.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filteredEntities.length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-foreground-tertiary text-sm mb-2">
          <Link href="/command-center" className="hover:text-accent">
            Command Center
          </Link>
          <span>/</span>
          <span>Entities</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Entities</h1>
            <p className="text-foreground-tertiary mt-1">
              {isFiltered ? (
                <>{filteredCount} of {total} {total === 1 ? 'entity' : 'entities'} shown</>
              ) : (
                <>{total} {total === 1 ? 'entity' : 'entities'} in this workspace</>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-accent text-accent-foreground px-4 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors flex items-center gap-2"
          >
            <span>+</span>
            <span>New Entity</span>
          </button>
        </div>

        {/* Filters */}
        <EntityFilters
          filters={filters}
          onChange={setFilters}
          availableTags={availableTags}
        />

        {/* Select All / Bulk Selection Header */}
        {!loading && filteredEntities.length > 0 && (
          <div className="flex items-center gap-4 mb-4 px-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                  allSelected
                    ? 'bg-accent border-accent'
                    : someSelected
                    ? 'bg-accent-muted0 border-accent'
                    : 'border-border/30 hover:border-border-muted0'
                }`}
                onClick={toggleSelectAll}
              >
                {(allSelected || someSelected) && (
                  <svg className="w-3 h-3 text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {allSelected ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                    )}
                  </svg>
                )}
              </div>
              <span className="text-foreground-tertiary text-sm">
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : 'Select all'
                }
              </span>
            </label>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        )}

        {/* Empty state - no entities at all */}
        {!loading && entities.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/5 mb-4">
              <svg className="w-8 h-8 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <p className="text-foreground-tertiary text-lg">No entities yet</p>
            <p className="text-foreground-muted mt-2">Create your first entity to get started</p>
          </div>
        )}

        {/* Empty state - filters returned no results */}
        {!loading && entities.length > 0 && filteredEntities.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/5 mb-4">
              <svg className="w-8 h-8 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-foreground-tertiary text-lg">No entities match your filters</p>
            <p className="text-foreground-muted mt-2">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => setFilters({ search: '', types: [], status: 'all', tags: [] })}
              className="mt-4 text-accent hover:text-accent"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Entities grid */}
        {!loading && filteredEntities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntities.map((entity) => {
              const isSelected = selectedIds.has(entity.id);
              return (
                <div
                  key={entity.id}
                  onClick={() => setSelectedEntity(entity)}
                  className={`relative bg-surface/30 border rounded-lg p-4 hover:border-border transition-colors cursor-pointer group ${
                    isSelected ? 'border-border-muted0 bg-accent-muted' : 'border-border-muted'
                  }`}
                >
                  {/* Checkbox */}
                  <div
                    className="absolute top-3 left-3"
                    onClick={(e) => toggleSelect(entity.id, e)}
                  >
                    <div
                      className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-accent border-accent'
                          : 'border-border/30 hover:border-border-muted0 group-hover:border-border/40'
                      }`}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="pl-8">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-foreground font-semibold truncate">{entity.name}</h3>
                        <p className="text-foreground-tertiary text-sm mt-0.5">
                          {ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type}
                        </p>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 text-xs rounded border ${
                        entity.status === 'active'
                          ? 'bg-green-900/50 text-green-400 border-green-500/30'
                          : entity.status === 'deprecated'
                          ? 'bg-red-900/50 text-red-400 border-red-500/30'
                          : 'bg-gray-900/50 text-gray-400 border-gray-500/30'
                      }`}>
                        {entity.status}
                      </span>
                    </div>
                    {entity.description && (
                      <p className="text-foreground-muted text-sm mt-2 line-clamp-2">{entity.description}</p>
                    )}
                    {entity.tags && entity.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {entity.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="px-1.5 py-0.5 bg-background text-foreground-tertiary text-xs rounded">
                            {tag}
                          </span>
                        ))}
                        {entity.tags.length > 3 && (
                          <span className="text-foreground-muted text-xs">+{entity.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedIds.size > 0 && (
          <BulkActionsToolbar
            selectedCount={selectedIds.size}
            selectedEntities={selectedEntities}
            onClearSelection={clearSelection}
            onBulkDelete={handleBulkDelete}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkAddTags={handleBulkAddTags}
            onBulkRemoveTags={handleBulkRemoveTags}
            availableTags={availableTags}
          />
        )}

        {/* Create Entity Modal */}
        {showCreateModal && (
          <CreateEntityModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              loadEntities();
            }}
          />
        )}

        {/* Entity Detail Modal */}
        {selectedEntity && workspaceId && (
          <EntityDetailModal
            entity={selectedEntity}
            workspaceId={workspaceId}
            onClose={() => setSelectedEntity(null)}
            onDelete={async () => {
              await deleteEntity(selectedEntity.id);
              setSelectedEntity(null);
              loadEntities();
            }}
            onUpdated={(updated) => {
              setSelectedEntity(updated);
              loadEntities();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default function EntitiesPage() {
  return (
    <WorkspaceProvider>
      <CommandCenterProvider>
        <CommandCenterHeader />
        <EntitiesContent />
      </CommandCenterProvider>
    </WorkspaceProvider>
  );
}
